// Reporting-app state hooks.
//
// Replaces the old ReportingContext. Redux is the source of truth for
// teamId/sessionId (via reportingSlice) plus region/org (shared slices).
// URL query params (?region=&org=&team=&session=) round-trip with Redux:
// the setters write URL on dispatch; useReportingUrlSync() reads URL on
// mount + on popstate.
import { useEffect, useMemo, useRef } from 'react'
import { useLocation, useSearchParams } from 'react-router-dom'
import {
  useAppDispatch,
  useAppSelector,
  useReportingTeams,
  useReportingTeamSessions,
  useSelectedOrganisationId
} from '@frontend/react'
import {
  selectReportingSessionId,
  selectReportingTeamId,
  setReportingSessionId,
  setReportingTeamId
} from '@frontend/core/lib/domains/reporting'
import {
  setSelectedOrganisationId,
  useOrganisations_getAllQuery
} from '@frontend/core/lib/domains/organisations'
import {
  selectAllRegions,
  selectSelectedRegion,
  setSelectedRegionId
} from '@frontend/core/lib/domains/regions'
import { normaliseSportType } from '@frontend/core/lib/domains/reporting/types'
import { getReportingSportConfig } from './sport-config'
import { useReportingUnitSystem } from './use-reporting-unit-system'
import type { ReportingSportConfig } from './sport-config'
import type {
  ReportingSession,
  ReportingTeam,
  ReportingSport
} from '@frontend/core/lib/domains/reporting/types'
import type { regions as regionsData } from '@frontend/core/lib/domains/regions/data_types'

export interface ReportingStateValue {
  teamId: string
  sessionId: string
  team: ReportingTeam | undefined
  session: ReportingSession | undefined
  sport: ReportingSport
  config: ReportingSportConfig
  isLoading: boolean
}

export function useReportingState(): ReportingStateValue {
  const teamId = useAppSelector(selectReportingTeamId) || ''
  const sessionId = useAppSelector(selectReportingSessionId) || ''

  const { map: teamsMap, isLoading: teamsLoading } = useReportingTeams()
  const { map: sessionsMap, isLoading: sessionsLoading } =
    useReportingTeamSessions(teamId)

  const team = teamId ? teamsMap[teamId] : undefined
  const session = sessionId ? sessionsMap[sessionId] : undefined
  const rawSport = session?.sport ?? team?.sport
  const sport = normaliseSportType(rawSport)
  const unitSystem = useReportingUnitSystem(sport)

  return useMemo(() => {
    const config = getReportingSportConfig(sport, unitSystem)
    const isLoading = teamsLoading || (!!teamId && sessionsLoading)
    return { teamId, sessionId, team, session, sport, config, isLoading }
  }, [
    teamId,
    sessionId,
    team,
    session,
    sport,
    unitSystem,
    teamsLoading,
    sessionsLoading
  ])
}

// Body-level theme class — overrides --primary/--ring in libs/ui/src/lib/ui.css
// so active nav pill, focus rings, and chart-1 recolor per sport. Previously
// lived in ReportingProvider.
export function useReportingBodyTheme() {
  const { config } = useReportingState()
  useEffect(() => {
    document.body.classList.add(config.themeClass)
    return () => document.body.classList.remove(config.themeClass)
  }, [config.themeClass])
}

// Map each reporting route to the human label shown in `document.title` and
// browser history. Kept colocated with the title hook so updates happen in
// one place when routes change.
const VIEW_LABEL_BY_PATHNAME: Record<string, string> = {
  '/dashboard': 'Dashboard',
  '/team': 'Team',
  '/player': 'Player'
}

function getViewLabel(pathname: string): string {
  // `/player/:playerId` is the only sub-route; treat it as the player view.
  if (pathname.startsWith('/player')) return 'Player'
  return VIEW_LABEL_BY_PATHNAME[pathname] ?? 'Reporting'
}

const titleDateFormatter = new Intl.DateTimeFormat(undefined, {
  year: 'numeric',
  month: 'short',
  day: 'numeric'
})

// Updates document.title so browser tabs and history entries describe the
// current view. Format: `View - Team - Session | Reporting | Sportable`.
// Team/session are omitted when nothing is selected yet.
export function useReportingPageTitle() {
  const { pathname } = useLocation()
  const { team, session } = useReportingState()

  useEffect(() => {
    const view = getViewLabel(pathname)
    const sessionLabel = session
      ? [
          titleDateFormatter.format(new Date(session.date)),
          session.type,
          session.name
        ]
          .filter(Boolean)
          .join(' ')
      : ''
    const parts = [view, team?.name, sessionLabel].filter(Boolean)
    document.title = `${parts.join(' - ')} | Reporting | Sportable`
  }, [pathname, team?.name, session])
}

// Bridge Redux ↔ URL for reporting. Three effects:
//
//  1. seed: on mount, read the URL once and dispatch initial Redux values
//  2. writeback: when any of the 4 scope values in Redux change, update the
//     URL via react-router's setSearchParams so react-router's own view
//     stays in sync (slice reducers no longer write URL directly — doing so
//     via history.replaceState bypasses react-router's listener and made
//     view-level setSearchParams calls clobber scope values)
//  3. popstate: browser back/forward button triggers a fresh read → dispatch
export function useReportingUrlSync() {
  const dispatch = useAppDispatch()
  const [, setSearchParams] = useSearchParams()

  const region = useAppSelector(selectSelectedRegion)
  const orgId = useSelectedOrganisationId()
  const teamId = useAppSelector(selectReportingTeamId)
  const sessionId = useAppSelector(selectReportingSessionId)

  // Seed Redux from URL on mount only.
  useEffect(() => {
    const seed = () => {
      const params = new URLSearchParams(window.location.search)
      const urlRegion = params.get('region')
      const urlOrg = params.get('org')
      const urlTeam = params.get('team')
      const urlSession = params.get('session')
      if (urlRegion) {
        dispatch(setSelectedRegionId(urlRegion as keyof typeof regionsData))
      }
      if (urlOrg) dispatch(setSelectedOrganisationId(urlOrg))
      if (urlTeam) dispatch(setReportingTeamId(urlTeam))
      if (urlSession) dispatch(setReportingSessionId(urlSession))
    }
    seed()
    window.addEventListener('popstate', seed)
    return () => window.removeEventListener('popstate', seed)
  }, [dispatch])

  // Scope-specific search params that only make sense inside a particular
  // team/session (drill id points at one session, player tab choice is
  // specific to a session's data shape). Clear these when scope changes so
  // the next team/session doesn't inherit a drill/tab that no longer
  // exists.
  const SCOPE_SCOPED_PARAMS = ['drill', 'tab'] as const
  const prevScopeRef = useRef<{
    region?: string
    org?: string | null
    team?: string | null
    session?: string | null
  }>({})

  // Write Redux → URL via react-router so both stay in sync.
  useEffect(() => {
    const prev = prevScopeRef.current
    const scopeChanged =
      prev.region !== undefined &&
      (prev.region !== region?.id ||
        prev.org !== orgId ||
        prev.team !== teamId ||
        prev.session !== sessionId)

    setSearchParams(
      (prevParams) => {
        const next = new URLSearchParams(prevParams)
        if (region?.id) next.set('region', region.id)
        else next.delete('region')
        if (orgId) next.set('org', orgId)
        else next.delete('org')
        if (teamId) next.set('team', teamId)
        else next.delete('team')
        if (sessionId) next.set('session', sessionId)
        else next.delete('session')
        if (scopeChanged) {
          for (const k of SCOPE_SCOPED_PARAMS) next.delete(k)
        }
        return next
      },
      { replace: true }
    )

    prevScopeRef.current = {
      region: region?.id,
      org: orgId,
      team: teamId,
      session: sessionId
    }
  }, [region?.id, orgId, teamId, sessionId, setSearchParams])
}

// Auto-select the first available region / org / team / session when nothing
// is selected. Replaces the scattered auto-pick effects that used to live in
// each switcher component.
export function useReportingAutoSelect() {
  const dispatch = useAppDispatch()
  const regions = useAppSelector(selectAllRegions)
  const selectedRegion = useAppSelector(selectSelectedRegion)
  const selectedOrgId = useSelectedOrganisationId()
  const orgsQuery = useOrganisations_getAllQuery(undefined, {})
  const organisations = useMemo(
    () => Object.values(orgsQuery.data?.entities ?? {}),
    [orgsQuery.data?.entities]
  )
  const teamId = useAppSelector(selectReportingTeamId)
  const sessionId = useAppSelector(selectReportingSessionId)
  const teamsQuery = useReportingTeams()
  const sessionsQuery = useReportingTeamSessions(teamId || '')

  useEffect(() => {
    if (!selectedRegion && regions.length > 0) {
      dispatch(setSelectedRegionId(regions[0].id))
    }
  }, [selectedRegion, regions, dispatch])

  // Skip picking from possibly-stale data while a query is fetching (e.g.
  // immediately after a region/org cascade clears the cache). Without this
  // guard, the first-available-in-list rule would immediately re-pick the
  // cached previous-scope values and undo the cascade.
  useEffect(() => {
    if (orgsQuery.isFetching) return
    if (!selectedOrgId && organisations.length > 0) {
      dispatch(setSelectedOrganisationId(organisations[0].id))
    }
  }, [selectedOrgId, organisations, orgsQuery.isFetching, dispatch])

  useEffect(() => {
    if (teamsQuery.isFetching) return
    if (!teamId && teamsQuery.list.length > 0) {
      dispatch(setReportingTeamId(teamsQuery.list[0].id))
    }
  }, [teamId, teamsQuery.list, teamsQuery.isFetching, dispatch])

  useEffect(() => {
    if (sessionsQuery.isFetching) return
    if (teamId && !sessionId && sessionsQuery.list.length > 0) {
      dispatch(setReportingSessionId(sessionsQuery.list[0].id))
    }
  }, [
    teamId,
    sessionId,
    sessionsQuery.list,
    sessionsQuery.isFetching,
    dispatch
  ])
}
